/**
 * EVA base thickness rules.
 *
 * Same placement rule as `shellThicknessRules`: the constants and the pure
 * functions live here, in a module free of React. The React-free consumers
 * are `prescriptionFormDefaults` (the default for a NEW prescription),
 * `prescriptionLegality` (the submit gate), the Excel exporter and the
 * workshop PDF (the printed value), and none of them may import the
 * component tree, or it drags in the eager `@/lib/supabase` client and stops
 * being unit-testable with plain fixtures.
 *
 * The field is a free number typed into a text input: 2 to 6 mm with at most
 * one decimal (2, 2.5, 4.3). It was a closed set of whole millimetres until
 * 2026-09-22; the integers that era stored are still valid. It is OPTIONAL
 * on read: every EVA line written before it existed has no value, and those
 * lines must stay saveable and must print "Not specified" rather than a
 * guessed number. The default below is applied to new prescriptions only
 * (see DEFAULT_PRESCRIPTION_STATE). Hydration never seeds it onto an
 * existing line, and the one route that merges the default under a copied
 * design (the wizard's reorder / reuse prefill in NewOrderPage) strips it
 * again unless the copied design carries its own (mergeDesignForPrefill.ts).
 *
 * Nothing here rounds or clamps. A value off the rule (a stored 7, a typed
 * 2.55) is kept exactly as it is, shown as typed with the message, and
 * blocks submit until the podiatrist corrects it: substituting a "nearby"
 * thickness would be a clinical decision made by a developer.
 */

export const EVA_THICKNESS_MIN_MM = 2;
export const EVA_THICKNESS_MAX_MM = 6;

export const EVA_THICKNESS_DEFAULT_MM = 2;

/** The one message for every way a value can be wrong. Shown inline under
 *  the input as the podiatrist types, and again by the submit / save gate. */
export const EVA_THICKNESS_PROBLEM_MESSAGE =
  'Enter a thickness between 2 and 6 mm with at most one decimal, for example 2.5';

/**
 * null when `v` is absent (the field is optional) or valid: within 2..6 and
 * carrying at most one decimal. Otherwise the message. The decimal test is
 * an exact round trip through toFixed(1): every k/10 comes back as itself
 * (2.3 is stored as 2.29999..., and "2.3" is what toFixed gives it), while
 * 2.55 comes back as 2.5 and 2.0000000001 as 2, so both are rejected. A
 * tolerance-based check would let 2.0000000001 through.
 */
export function evaThicknessProblem(v: number | undefined): string | null {
  if (v === undefined) return null;
  if (!Number.isFinite(v)) return EVA_THICKNESS_PROBLEM_MESSAGE;
  if (v < EVA_THICKNESS_MIN_MM || v > EVA_THICKNESS_MAX_MM) return EVA_THICKNESS_PROBLEM_MESSAGE;
  if (Number(v.toFixed(1)) !== v) return EVA_THICKNESS_PROBLEM_MESSAGE;
  return null;
}

/**
 * What the input admits while typing: digits and at most one decimal
 * separator, dot or comma (the phone's decimal keypad shows one or the other
 * by locale). Anything else, a letter or a second separator, is simply not
 * entered. Intermediate drafts such as "2." and "2," are admitted so the
 * podiatrist can keep typing.
 */
export function isEvaThicknessDraft(raw: string): boolean {
  return /^\d*[.,]?\d*$/.test(raw);
}

/**
 * The number a draft stands for, or undefined when it stands for nothing
 * (empty, or a lone separator). A comma is read as the decimal point. No
 * rounding, no clamping: "2.55" is 2.55 and "6.1" is 6.1, and the caller
 * decides what to do with them (evaThicknessProblem).
 */
export function parseEvaThicknessInput(raw: string): number | undefined {
  const normalised = raw.trim().replace(',', '.');
  if (normalised === '' || normalised === '.') return undefined;
  const n = Number(normalised);
  return Number.isFinite(n) ? n : undefined;
}

/** "2", "2.5", never "2.0": what the input shows for a stored number and
 *  what the lab sheet and the workshop PDF print before their " mm". */
export function formatEvaThicknessMm(v: number): string {
  return String(v);
}
